import { apiFetch, API_BASE } from './client';

export interface SalesSummary {
  period: { from: string; to: string };
  totals: {
    invoiceCount: number;
    subtotal: number;
    taxTotal: number;
    total: number;
    collected: number;
    outstanding: number;
  };
  byMonth: { month: string; invoiceCount: number; subtotal: number; taxTotal: number; total: number; collected: number; outstanding: number }[];
  byDocType: { docType: string; count: number; total: number }[];
  quotations: {
    total: number; pending: number; won: number; lost: number; expired: number;
    pipelineValue: number; wonValue: number; lostValue: number; winRate: number;
  };
}

export interface HsnRow {
  hsnSac: string;
  description: string;
  uqc: string;
  totalQty: number;
  totalTaxable: number;
  gstRate: number;
  igst: number;
  cgst: number;
  sgst: number;
}

export interface CashLedgerEntry {
  date: string;
  type: 'receipt' | 'expense' | 'purchase_payment';
  description: string;
  debit: number;
  credit: number;
  reference: string;
  balance: number;
}

export interface CashLedgerReport {
  period: { from: string; to: string };
  summary: { totalReceipts: number; totalExpenses: number; closingBalance: number };
  entries: CashLedgerEntry[];
}

export interface OutstandingReport {
  asOf: string;
  summary: { label: string; count: number; total: number; outstanding: number }[];
  parties: {
    partyId: string; partyName: string; invoiceCount: number;
    totalInvoiced: number; totalPaid: number; outstanding: number; oldestDueDays: number;
    buckets: { current: number; days1to30: number; days31to60: number; days61to90: number; over90: number };
  }[];
}

async function downloadBlob(path: string, filename: string) {
  const token = sessionStorage.getItem('cl_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export const reportsApi = {
  salesSummary: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return apiFetch<SalesSummary>(`/reports/sales-summary${qs ? '?' + qs : ''}`);
  },
  outstanding: () => apiFetch<OutstandingReport>('/reports/outstanding'),
  gstr1: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch<unknown>(`/reports/gstr1?${params.toString()}`);
  },
  gstr1Json: (from?: string, to?: string) =>
    downloadBlob(`/reports/gstr1/json${from ? `?from=${from}&to=${to}` : ''}`, `GSTR1_${from ?? 'all'}.json`),
  gstr3bCsv: (from?: string, to?: string) =>
    downloadBlob(`/reports/gstr3b/csv${from ? `?from=${from}&to=${to}` : ''}`, `GSTR3B_${from ?? 'all'}.csv`),
  salesRegisterCsv: (from?: string, to?: string) =>
    downloadBlob(`/reports/sales-register/csv${from ? `?from=${from}&to=${to}` : ''}`, `SalesRegister_${from ?? 'all'}.csv`),
  hsnSummary: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch<HsnRow[]>(`/reports/hsn-summary?${params.toString()}`);
  },
  cashLedger: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch<CashLedgerReport>(`/reports/cash-ledger?${params.toString()}`);
  },
  // Real interest income AND expense for a date range — feeds the P&L's
  // "Other Income" and "Finance Cost" lines. Named otherIncomeSummary/
  // other-income-summary before the Loan Account rework (see CLAUDE.md's
  // Loans & Capital note) — renamed since "other income" no longer
  // describes an expense field.
  interestSummary: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch<{ interestIncome: number; interestExpense: number }>(`/reports/interest-summary?${params.toString()}`);
  },
  gstr2aReconcile: (body: Record<string, unknown>) =>
    apiFetch<unknown>('/reports/gstr2a/reconcile', { method: 'POST', body: JSON.stringify(body) }),
  balanceSheet: (asOf?: string) =>
    apiFetch<{
      asOf: string;
      assets: { cash: number; receivables: number; stock: number; fixedAssets: number; total: number };
      liabilities: { payables: number; gstPayable: number; securedLoans: number; unsecuredLoans: number; total: number };
      equity: { netProfit: number; capitalAccount: number; total: number };
      liabilitiesPlusEquity: number;
    }>(`/reports/balance-sheet${asOf ? `?asOf=${asOf}` : ''}`),
};
